import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useLoad, err, Table, inputCls, Field, Sel } from "./kit";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function HolidayCalendar() {
  const { can, isAdmin } = useAuth();
  const manage = isAdmin || can("HR", "view");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [d, reload] = useLoad(`/duty/calendar?year=${year}`);
  const [f, setF] = useState({ kind: "Company holiday" });
  const add = () => api.post("/duty/holidays", f).then(() => { toast.success("Holiday added"); setF({ kind: "Company holiday" }); reload(); }).catch(err);
  const del = (id) => api.delete(`/duty/holidays/${id}`).then(() => { toast.success("Removed"); reload(); }).catch(err);
  const toggle = (i) => {
    const days = d.weekly_off.includes(i) ? d.weekly_off.filter((x) => x !== i) : [...d.weekly_off, i];
    api.put("/duty/weekly-off", { days }).then(() => { toast.success("Weekly off updated"); reload(); }).catch(err);
  };
  if (!d) return null;
  return (
    <div className="space-y-5" data-testid="holiday-calendar">
      <div><h2 className="text-lg font-extrabold">Holidays & weekly offs</h2>
        <p className="text-xs text-zinc-500">Studio attendance is not required on these days, and leave and event-duty requests count only working days.</p></div>
      <div className="rounded-xl border border-[#D6C6B0] bg-white/70 p-4">
        <div className="mb-2 text-xs font-semibold text-zinc-600">Weekly off days</div>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((n, i) => (
            <button key={n} disabled={!manage} data-testid={`weekly-off-${i}`} onClick={() => toggle(i)}
              className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${d.weekly_off.includes(i) ? "bg-[#8B7356] text-white" : "bg-[#F3ECE2] text-zinc-600"} ${manage ? "hover:opacity-80" : "cursor-default"}`}>{n}</button>
          ))}
        </div>
      </div>
      {manage && (
        <div className="grid grid-cols-1 items-end gap-2 rounded-xl border border-[#D6C6B0] bg-white/70 p-4 sm:grid-cols-4">
          <Field label="Date"><input data-testid="holiday-date" type="date" className={inputCls} value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Holiday name"><input data-testid="holiday-name" className={inputCls} value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Type"><Sel testid="holiday-kind" value={f.kind} onChange={(v) => setF({ ...f, kind: v || "Company holiday" })} options={["Company holiday", "National holiday", "Festival", "Optional holiday"]} /></Field>
          <Button disabled={!f.date || !f.name} onClick={add} data-testid="holiday-add">Add holiday</Button>
        </div>
      )}
      <div className="flex items-center gap-2 text-xs"><span>Year</span><div className="w-28"><Sel testid="holiday-year" value={year} onChange={(v) => setYear(v || String(new Date().getFullYear()))} options={[-1, 0, 1, 2].map((i) => String(new Date().getFullYear() + i))} placeholder="Year" /></div></div>
      <Table testid="holidays-table" rows={d.holidays} empty="No holidays added for this year yet."
        cols={[{ k: "date", l: "Date" }, { k: "day", l: "Day", r: (h) => DAYS[new Date(`${h.date}T00:00`).getDay()] }, { k: "name", l: "Holiday" }, { k: "kind", l: "Type" },
          ...(manage ? [{ k: "x", l: "", r: (h) => <button data-testid={`holiday-delete-${h.id}`} onClick={() => del(h.id)} className="text-zinc-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button> }] : [])]} />
    </div>
  );
}

export function useWorkingDays() {
  const [d] = useLoad("/duty/calendar");
  return (from, to) => {
    if (!d || !from || !to) return "—";
    const hol = new Set(d.holidays.map((h) => h.date));
    let n = 0;
    for (let x = new Date(`${from}T00:00`); x <= new Date(`${to}T00:00`); x.setDate(x.getDate() + 1)) {
      const iso = `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
      if (!hol.has(iso) && !d.weekly_off.includes(x.getDay())) n += 1;
    }
    return n;
  };
}
